/**
 * React Query hooks for ProcessoParceiro
 *
 * Story: 11.5 - Processos Atribuídos
 * Pattern: React Query v4+ with TypeScript
 */
import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
  UseQueryResult,
  UseMutationResult,
} from "@tanstack/react-query";
import { toast } from "sonner";
import processoParceiroService from "../services/processoParceiroService";
import {
  ProcessoListResponse,
  ProcessoParceiro,
  ProcessoFilterParams,
  TimelineListResponse,
  PagamentosByProcessoResponse,
  AddDocumentoRequest,
} from "../types/processo-parceiro";

// Query keys
export const processoQueryKeys = {
  all: ["processos"] as const,
  lists: () => [...processoQueryKeys.all, "list"] as const,
  list: (filters?: ProcessoFilterParams, page?: number) =>
    [...processoQueryKeys.lists(), { filters, page }] as const,
  details: () => [...processoQueryKeys.all, "detail"] as const,
  detail: (id: string) => [...processoQueryKeys.details(), id] as const,
  timeline: (id: string, page?: number) =>
    [...processoQueryKeys.all, "timeline", id, { page }] as const,
  pagamentos: (id: string) => [...processoQueryKeys.all, "pagamentos", id] as const,
};

/**
 * Hook: Get paginated list of processos with filters
 *
 * Features:
 * - Refetches every 60s (processos can change)
 * - Keeps previous data while refetching
 * - Stale time: 30s
 *
 * @param filters - Filter parameters
 * @param page - Page number
 * @param pageSize - Items per page
 */
export const useProcessosList = (
  filters?: ProcessoFilterParams,
  page: number = 1,
  pageSize: number = 20
): UseQueryResult<ProcessoListResponse, Error> => {
  return useQuery({
    queryKey: processoQueryKeys.list(filters, page),
    queryFn: () => processoParceiroService.getProcessos(filters, page, pageSize),
    staleTime: 30000, // 30s
    refetchInterval: 60000, // Refetch every 60s
    placeholderData: keepPreviousData,
  });
};

/**
 * Hook: Get complete processo details
 *
 * Features:
 * - Refetches on window focus
 * - Stale time: 30s
 *
 * @param processoId - UUID of the processo
 * @param enabled - Whether to enable the query (default: true)
 */
export const useProcessoDetails = (
  processoId: string,
  enabled: boolean = true
): UseQueryResult<ProcessoParceiro, Error> => {
  return useQuery({
    queryKey: processoQueryKeys.detail(processoId),
    queryFn: () => processoParceiroService.getProcessoDetails(processoId),
    enabled: enabled && !!processoId,
    staleTime: 30000, // 30s
    refetchOnWindowFocus: true,
  });
};

/**
 * Hook: Get timeline events for a processo
 *
 * Features:
 * - Paginated timeline
 * - Stale time: 30s
 *
 * @param processoId - UUID of the processo
 * @param page - Page number
 * @param pageSize - Items per page
 * @param enabled - Whether to enable the query
 */
export const useProcessoTimeline = (
  processoId: string,
  page: number = 1,
  pageSize: number = 50,
  enabled: boolean = true
): UseQueryResult<TimelineListResponse, Error> => {
  return useQuery({
    queryKey: processoQueryKeys.timeline(processoId, page),
    queryFn: () => processoParceiroService.getProcessoTimeline(processoId, page, pageSize),
    enabled: enabled && !!processoId,
    staleTime: 30000, // 30s
  });
};

/**
 * Hook: Get pagamentos for a processo
 *
 * Features:
 * - Includes totals (total_pago, total_pendente)
 * - Stale time: 30s
 *
 * @param processoId - UUID of the processo
 * @param enabled - Whether to enable the query
 */
export const useProcessoPagamentos = (
  processoId: string,
  enabled: boolean = true
): UseQueryResult<PagamentosByProcessoResponse, Error> => {
  return useQuery({
    queryKey: processoQueryKeys.pagamentos(processoId),
    queryFn: () => processoParceiroService.getProcessoPagamentos(processoId),
    enabled: enabled && !!processoId,
    staleTime: 30000, // 30s
  });
};

/**
 * Hook: Mutation to add document to processo
 *
 * Features:
 * - Invalidates processo details and timeline queries on success
 * - Shows toast notifications
 *
 * @param processoId - UUID of the processo
 */
export const useAddDocumentoToProcesso = (
  processoId: string
): UseMutationResult<ProcessoParceiro, Error, AddDocumentoRequest> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: AddDocumentoRequest) =>
      processoParceiroService.addDocumentoToProcesso(processoId, request),
    onSuccess: (data) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: processoQueryKeys.detail(processoId) });
      queryClient.invalidateQueries({ queryKey: processoQueryKeys.timeline(processoId) });
      queryClient.invalidateQueries({ queryKey: processoQueryKeys.lists() });

      toast.success("Documento adicionado com sucesso!");
    },
    onError: (error: Error) => {
      // O service (handleApiError) já entrega a mensagem em português, sem `.response`.
      toast.error(error.message || "Erro ao adicionar documento");
    },
  });
};
